import { fetcher, SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import {
  GetSubmissionResponse,
  ReviewSubmissionDTO,
  ReviewSubmissionResponse,
  SubmissionStatusFilter,
} from './types';

import { ExemptionCertificateSubmission } from '@commenda/prisma';
import {
  CreateExemptionCertificate,
  CreateExemptionCertificateByBuyer,
  ExemptionCertificateResponse,
} from '../new-sales-tax/exemptions/types';

export async function useCreateExemptionPortalSubmission(
  data: CreateExemptionCertificateByBuyer,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: ExemptionCertificateSubmission;
  error?: string;
}> {
  try {
    const response = await fetcher(
      'portal/customer/submissions',
      {
        method: 'POST',
        body: data,
      },
      undefined,
      clerkToken
    );

    const responseData = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: responseData.message || 'Failed to create submission',
      };
    }

    return {
      success: true,
      data: responseData as ExemptionCertificateSubmission,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export async function useUpdateExemptionPortalSubmissionById(
  submissionId: string,
  updateSubmissionDto: CreateExemptionCertificateByBuyer,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: ExemptionCertificateSubmission;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/customer/submissions/${submissionId}`,
      {
        method: 'PUT',
        body: updateSubmissionDto,
      },
      undefined,
      clerkToken
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to update submission',
      };
    }

    return {
      success: true,
      data: data as ExemptionCertificateSubmission,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useExemptionPortalCustomerSubmissions(
  clerkToken: string,
  status?: SubmissionStatusFilter
): {
  submissions: GetSubmissionResponse[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: GetSubmissionResponse[]
  ) => Promise<GetSubmissionResponse[] | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<GetSubmissionResponse[]>(
    clerkToken
      ? status
        ? `portal/customer/submissions?status=${status}`
        : 'portal/customer/submissions'
      : null,
    clerkToken ? SWRFetcher({ method: 'GET' }, undefined, clerkToken) : null
  );

  return {
    submissions: data,
    isLoading,
    error,
    mutate,
  };
}

export function useExemptionPortalCustomerSubmissionById(
  submissionId: string,
  clerkToken: string
): {
  submission: GetSubmissionResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: GetSubmissionResponse
  ) => Promise<GetSubmissionResponse | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<GetSubmissionResponse>(
    submissionId ? `portal/customer/submissions/${submissionId}` : null,
    clerkToken ? SWRFetcher({ method: 'GET' }, undefined, clerkToken) : null
  );

  return {
    submission: data,
    isLoading,
    error,
    mutate,
  };
}

// Seller APIs

export async function useCreateExemptionPortalSubmissionBySeller(
  body: CreateExemptionCertificate
): Promise<{
  success: boolean;
  data?: ExemptionCertificateResponse;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/sellers/${body.corporation_id}/submissions`,
      {
        method: 'POST',
        body,
      },
      undefined
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to create submission',
      };
    }

    return {
      success: true,
      data: data as ExemptionCertificateResponse,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export async function useUpdateExemptionPortalSubmissionBySeller(
  submissionId: string,
  body: CreateExemptionCertificate
): Promise<{
  success: boolean;
  data?: ExemptionCertificateSubmission;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/sellers/${body.corporation_id}/submissions/${submissionId}`,
      {
        method: 'POST',
        body,
      },
      undefined
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to create submission',
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export async function useDeleteExemptionPortalSubmissionBySeller(
  submissionId: string,
  corporationId: number
): Promise<{
  success: boolean;
  error: string;
}> {
  try {
    const response = await fetcher(
      `portal/sellers/${corporationId}/submissions/${submissionId}`,
      {
        method: 'DELETE',
      },
      undefined
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to delete submission',
      };
    }

    return {
      success: true,
      error: '',
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useExemptionPortalSellerSubmissions(
  corporationId: number,
  status?: SubmissionStatusFilter | null
): {
  submissions: GetSubmissionResponse[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: GetSubmissionResponse[]
  ) => Promise<GetSubmissionResponse[] | undefined>;
} {
  const query = new URLSearchParams();

  if (status) {
    query.append('status', status);
  }

  const { data, error, isLoading, mutate } = useSWR<GetSubmissionResponse[]>(
    corporationId
      ? `portal/sellers/${corporationId}/submissions?${query}`
      : null,
    SWRFetcher({ method: 'GET' }, undefined)
  );

  return {
    submissions: data,
    isLoading,
    error,
    mutate,
  };
}

export async function useReviewExemptionPortalSubmission(
  submissionId: string,
  reviewSubmissionDto: ReviewSubmissionDTO,
  corporationId: number
): Promise<{
  success: boolean;
  data?: ReviewSubmissionResponse;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/sellers/${corporationId}/submissions/${submissionId}/review`,
      {
        method: 'POST',
        body: reviewSubmissionDto,
      },
      undefined
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to review submission',
      };
    }

    return {
      success: true,
      data: data as ReviewSubmissionResponse,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useExemptionPortalCustomerSubmissionsWithinSeller(
  sellerId: number,
  salesTaxCustomerId: string
): {
  submissions: GetSubmissionResponse[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: GetSubmissionResponse[]
  ) => Promise<GetSubmissionResponse[] | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<GetSubmissionResponse[]>(
    sellerId && salesTaxCustomerId
      ? `portal/sellers/${sellerId}/customers/${salesTaxCustomerId}/submissions`
      : null,
    SWRFetcher({ method: 'GET' }, undefined)
  );

  return {
    submissions: data,
    isLoading,
    error,
    mutate,
  };
}

export function useExemptionPortalSubmissionById(
  sellerId: number,
  submissionId: string
): {
  submission: GetSubmissionResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: GetSubmissionResponse
  ) => Promise<GetSubmissionResponse | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<GetSubmissionResponse>(
    sellerId && submissionId
      ? `portal/sellers/${sellerId}/submissions/${submissionId}`
      : null,
    SWRFetcher({ method: 'GET' }, undefined)
  );

  return {
    submission: data,
    isLoading,
    error,
    mutate,
  };
}
